import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, useSpring } from "framer-motion";

const CustomCursor = ({
  size = 48,
  borderColor = "#c1471f",
  backgroundColor = "rgba(193, 71, 31, 0.08)",
  dotSize = 6,
  dotColor = "#c1471f",
  hoverScale = 1.5,
  hoverBorderColor = "#9a3413",
  hoverBackgroundColor = "rgba(154, 52, 19, 0.22)",
  hoverDotColor = "#9a3413",
  disabled = false,
}) => {
  const springOptions = { damping: 25, stiffness: 400, mass: 0.1 };
  const x = useSpring(0, springOptions);
  const y = useSpring(0, springOptions);
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const frameRef = useRef(null);

  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const isClickableElement = useCallback((element) => {
    if (!element) return false;
    return (
      element.tagName?.toLowerCase() === "button" ||
      element.tagName?.toLowerCase() === "a" ||
      element.hasAttribute("data-clickable") ||
      Boolean(
        element.closest?.(
          "button, a, [data-clickable], [role='button'], label, summary, select"
        )
      )
    );
  }, []);

  useEffect(() => {
    if (disabled || prefersReducedMotion) return;

    const updateMousePosition = (e) => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = requestAnimationFrame(() => {
        x.set(e.clientX);
        y.set(e.clientY);
        if (!isVisible) setIsVisible(true);
      });
    };

    // mouseover also fires while the page scrolls under a still pointer, so
    // keep this cheap (no getComputedStyle) and only re-render on change.
    const handleMouseOver = (e) => {
      const next = isClickableElement(e.target);
      setIsHovering((prev) => (prev === next ? prev : next));
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseEnter = () => {
      setIsVisible(true);
    };

    window.addEventListener("mousemove", updateMousePosition, { passive: true });
    document.addEventListener("mouseover", handleMouseOver, { passive: true });
    document.addEventListener("mouseleave", handleMouseLeave, { passive: true });
    document.addEventListener("mouseenter", handleMouseEnter, { passive: true });

    const hideCursor = () => {
      document.body.style.cursor = "none";
      const style = document.createElement("style");
      style.id = "custom-cursor-style";
      style.textContent = `
        * {
          cursor: none !important;
        }
      `;
      document.head.appendChild(style);
    };

    hideCursor();

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      window.removeEventListener("mousemove", updateMousePosition);
      document.removeEventListener("mouseover", handleMouseOver);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mouseenter", handleMouseEnter);
      document.body.style.cursor = "auto";
      const style = document.getElementById("custom-cursor-style");
      if (style) {
        style.remove();
      }
    };
  }, [isClickableElement, disabled, prefersReducedMotion, isVisible, x, y]);

  if (disabled || prefersReducedMotion) return null;

  const cursorVariants = {
    default: {
      scale: 1,
      opacity: 1,
      transition: { type: "tween", duration: 0.15 },
    },
    hover: {
      scale: hoverScale,
      opacity: 0.8,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const dotVariants = {
    default: {
      opacity: 1,
      scale: 1,
      transition: { type: "tween", duration: 0.15 },
    },
    hover: {
      opacity: 1,
      scale: 1.2,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const baseCursorStyles = {
    position: "fixed",
    zIndex: 1000000,
    pointerEvents: "none",
    borderRadius: "50%",
    transform: "translate(-50%, -50%)",
    willChange: "transform",
    backfaceVisibility: "hidden",
    opacity: isVisible ? 1 : 0,
    transition: "opacity 0.2s ease",
    left: 0,
    top: 0,
    WebkitTransform: "translate(-50%, -50%)",
  };

  const cursorContent = (
    <>
      <motion.div
        className="custom-cursor"
        variants={cursorVariants}
        animate={isHovering ? "hover" : "default"}
        initial="default"
        style={{
          ...baseCursorStyles,
          height: size,
          width: size,
          border: `4px solid ${isHovering ? hoverBorderColor : borderColor}`,
          backgroundColor: isHovering ? hoverBackgroundColor : backgroundColor,
          x,
          y,
        }}
        aria-hidden="true"
      />

      <motion.div
        className="custom-cursor-dot"
        variants={dotVariants}
        animate={isHovering ? "hover" : "default"}
        initial="default"
        style={{
          ...baseCursorStyles,
          height: dotSize,
          width: dotSize,
          backgroundColor: isHovering ? hoverDotColor : dotColor,
          x,
          y,
        }}
        aria-hidden="true"
      />
    </>
  );

  return createPortal(cursorContent, document.body);
};

export default React.memo(CustomCursor);
